'use client';

import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { Card } from '@/components/ui/card';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';

export function BrandThemeSection() {
    const t = useTranslations('brandTheme');
    const locale = useLocale();
    const sectionRef = useScrollAnimation({ threshold: 0.12 });
    const content = {
        pillLabel: t('pillLabel'),
        heroTitle: t('heroTitle'),
        heroDescription: t('heroDescription'),
        cta: t('cta'),
        customTitle: t('customTitle'),
        customDescription: t('customDescription'),
        widgetsTitle: t('widgetsTitle'),
        widgetsDescription: t('widgetsDescription'),
        widgetsCta: t('widgetsCta'),
        heroImageAlt: t('heroImageAlt'),
        customImageAlt: t('customImageAlt'),
        widgetsImageAlt: t('widgetsImageAlt'),
    };
    const visibleClass = sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6';
    const cardHidden = '[&>div>h3]:hidden [&>div>p]:hidden [&>div>a]:hidden';

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="gradient"
            padding="lg"
            className="relative overflow-hidden bg-white dark:bg-gray-950"
        >
            <div className="pointer-events-none absolute inset-0 opacity-60">
                <div className="absolute -left-10 top-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
                <div className="absolute right-[-8%] top-1/3 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
                <div className="absolute -bottom-12 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
            </div>

            <div className="relative z-10 space-y-8">
                <div
                    className={`bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800/60 rounded-3xl p-8 md:p-10 lg:p-12 shadow-[0_24px_60px_rgba(15,23,42,0.12)] dark:shadow-[0_30px_80px_rgba(0,0,0,0.35)] transition-all duration-700 ${visibleClass}`}
                >
                    <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr]">
                        <div>
                            <SectionHeading
                                pill={content.pillLabel}
                                title={content.heroTitle}
                                subtitle={content.heroDescription}
                                isVisible={sectionRef.isVisible}
                                align="left"
                                maxWidthClass="max-w-3xl"
                                className="!mb-6"
                            />
                            <Button variant="primary" size="md" href={getLocalizedPagePath('free-mlm-software-demo', locale)} showArrow={true}>{content.cta}</Button>
                        </div>
                        <div className="bg-gray-100 dark:bg-gray-900/80 rounded-2xl border border-gray-200 dark:border-gray-800/70 p-4 md:p-6">
                            <Image src="/images/home/BrandDArktheme_Home.webp" alt="Custom Branded MLM Software - White Label Network Marketing Platform with Customizable Themes and Branding" width={720} height={420} className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>

                <div className={`grid gap-6 lg:grid-cols-[0.6fr_1.4fr] transition-all duration-700 ${visibleClass}`} style={{ transitionDelay: '180ms' }}>
                    <Card title="" description="" showFooterLink={false} className={`!bg-gradient-to-br !from-blue-600 !via-blue-500 !to-blue-600 !border-0 !shadow-[0_30px_80px_rgba(37,99,235,0.35)] !rounded-3xl overflow-hidden !p-0 ${cardHidden}`}>
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.14),transparent_35%)] pointer-events-none" />
                        <div className="relative z-10 p-8 md:p-10 flex flex-col h-full">
                            <Typography variant="h4" className="!text-2xl mb-3 !text-white">{content.customTitle}</Typography>
                            <Typography variant="p" className="!text-white/80 leading-relaxed mb-6">{content.customDescription}</Typography>
                            <div className="mt-auto bg-white rounded-2xl overflow-hidden border border-white/40 shadow-lg">
                                <Image src="/images/home/USERDB_Cloudtheme.webp" alt="Cloud-Based MLM Software Dashboard - User Database Management System for Network Marketing Companies" width={720} height={420} className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </Card>

                    <Card
                        title=""
                        description=""
                        showFooterLink={false}
                        className={`!bg-white dark:!bg-gray-900/75 !border-gray-200 dark:!border-gray-800/70 !shadow-[0_24px_60px_rgba(15,23,42,0.12)] dark:!shadow-[0_30px_80px_rgba(0,0,0,0.35)] !rounded-3xl overflow-hidden !p-0 ${cardHidden}`}
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(255,255,255,0.05),transparent_45%),radial-gradient(circle_at_80%_20%,rgba(59,130,246,0.08),transparent_40%)] pointer-events-none" />
                        <div className="relative z-10 grid gap-6 lg:grid-cols-2 items-center p-8 md:p-10">
                            <div>
                                <Typography variant="h4" className="!text-2xl mb-3 text-gray-900 dark:text-white">{content.widgetsTitle}</Typography>
                                <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">{content.widgetsDescription}</Typography>
                                <Button variant="primary" size="md" className='w-full sm:w-auto justify-center' href={getLocalizedPagePath('mlm-software-features', locale)} showArrow={true}>{content.widgetsCta}</Button>
                            </div>
                            <Image src="/images/home/Configurable-Features.webp" alt="Configurable MLM Software Features - Customizable Network Marketing Platform Modules and Widgets Dashboard" width={760} height={480} className="w-full  object-cover rounded-xl" />
                        </div>
                    </Card>
                </div>
            </div>
        </SectionWrapper>
    );
}

